import { ErrorHandler, Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AppError } from './app-error';
import { isChunkLoadError, promptReloadForNewVersion } from './chunk-load';
import { ToastService } from '../services/toast.service';

@Injectable({ providedIn: 'root' })
export class AppErrorHandler implements ErrorHandler {
  private readonly toast = inject(ToastService);
  private readonly transloco = inject(TranslocoService);

  handleError(error: unknown): void {
    // Stale-deploy chunk-load failure — the one non-HTTP error the user can act on.
    if (isChunkLoadError(error)) {
      console.error('[ChunkLoadError]', error);
      promptReloadForNewVersion(this.toast, (key) => this.transloco.translate(key));
      return;
    }

    if (error instanceof AppError) {
      console.error('[AppError]', error.code, error.message, error.cause);
      // Toast only errors with a real HTTP identity — a wrapped HttpErrorResponse (statusCode > 0)
      // or an envelope errorCode from unwrap()'s "200 but result:false" path. A bare Error becomes
      // AppError('UNKNOWN', …, 0): logged, never toasted. (A malformed 200 envelope carrying
      // neither errorCode nor statusCode also falls through to log-only — rare, and better than
      // surfacing a raw message the user can't act on.)
      const isHttp = error.statusCode > 0 || (!!error.code && error.code !== 'UNKNOWN');
      if (!error.handled && isHttp) {
        this.toast.error({
          title: error.message || this.transloco.translate('common.loadError'),
          description: error.code,
        });
      }
      return;
    }

    // Everything else — template bugs, extension noise, window.onerror / unhandledrejection routed
    // here by provideBrowserGlobalErrorListeners — is logged only. It used to be toasted.
    console.error('[UnhandledError]', error);
  }
}
